import type { BackendApiErrorUnion } from '../../data/error/backendApi/_internal/createBackendApiError';
import type { Schema as SchemaType } from 'effect';

import { makeGetAssociatedProducts } from '../../request/backendApi/product/makeGetAssociatedProducts';
import { makeGetProductBaseByCode } from '../../request/backendApi/product/makeGetProductBaseByCode';
import { makeGetProductByCode } from '../../request/backendApi/product/makeGetProductByCode';
import { makeGetProductExpandedByCode } from '../../request/backendApi/product/makeGetProductExpandedByCode';
import { makeGetProductsByCodes } from '../../request/backendApi/product/makeGetProductsByCodes';
import { makeGetProductsByContextId } from '../../request/backendApi/product/makeGetProductsByContextId';
import { makeGetProductsByTaxon } from '../../request/backendApi/product/makeGetProductsByTaxon';
import { makeGetProductVariantByCode } from '../../request/backendApi/product/makeGetProductVariantByCode';
import { makeGetProductVariantExpandedByCode } from '../../request/backendApi/product/makeGetProductVariantExpandedByCode';
import { makeGetProductVariantPriceHistory } from '../../request/backendApi/product/makeGetProductVariantPriceHistory';
import { makeGetSearchProducts } from '../../request/backendApi/product/makeGetSearchProducts';
import { makeListProductVariantByCodes } from '../../request/backendApi/product/makeListProductVariantByCodes';
import type { ProductModuleDeps } from './productModuleDeps';

export type {
  ProductAdapters,
  ProductModuleDeps,
  ProductModuleSchemas,
  ProductVariantAdapters,
} from './productModuleDeps';

export const makeProductModule = <
  AssociatedProducts extends SchemaType.Top,
  ProductBase extends SchemaType.Top,
  ProductShow extends SchemaType.Top,
  ProductExpanded extends SchemaType.Top,
  ProductsByCodes extends SchemaType.Top,
  ProductsByContextId extends SchemaType.Top,
  ProductsByTaxon extends SchemaType.Top,
  ProductVariantShow extends SchemaType.Top,
  ProductVariantsByCodes extends SchemaType.Top,
  ProductVariantExpanded extends SchemaType.Top,
  ProductVariantPriceHistory extends SchemaType.Top,
  SearchProducts extends SchemaType.Top,
>(
  deps: ProductModuleDeps<
    AssociatedProducts,
    ProductBase,
    ProductShow,
    ProductExpanded,
    ProductsByCodes,
    ProductsByContextId,
    ProductsByTaxon,
    ProductVariantShow,
    ProductVariantsByCodes,
    ProductVariantExpanded,
    ProductVariantPriceHistory,
    SearchProducts
  >
) => {
  const associatedProducts = makeGetAssociatedProducts({
    schemas: { associatedProducts: deps.schemas.associatedProducts },
    rawUrlEncode: deps.rawUrlEncode,
  });
  const productBase = makeGetProductBaseByCode({
    schemas: { productBase: deps.schemas.productBase },
    adapters: { productBase: deps.adapters.productBase },
    rawUrlEncode: deps.rawUrlEncode,
  });
  const product = makeGetProductByCode({
    schemas: { productShow: deps.schemas.productShow },
    adapters: { productShow: deps.adapters.productShow },
    rawUrlEncode: deps.rawUrlEncode,
  });
  const productExpanded = makeGetProductExpandedByCode({
    schemas: { productExpanded: deps.schemas.productExpanded },
    adapters: { productExpanded: deps.adapters.productExpanded },
    rawUrlEncode: deps.rawUrlEncode,
  });
  const productsByCodes = makeGetProductsByCodes({
    schemas: { productsByCodes: deps.schemas.productsByCodes },
  });
  const productsByContextId = makeGetProductsByContextId({
    schemas: { productsByContextId: deps.schemas.productsByContextId },
  });
  const productsByTaxon = makeGetProductsByTaxon({
    schemas: { productsByTaxon: deps.schemas.productsByTaxon },
    rawUrlEncode: deps.rawUrlEncode,
  });
  const productVariant = makeGetProductVariantByCode({
    schemas: { productVariantShow: deps.schemas.productVariantShow },
    adapters: { productVariantShow: deps.adapters.productVariantShow },
    rawUrlEncode: deps.rawUrlEncode,
  });
  const productVariants = makeListProductVariantByCodes({
    schemas: { productVariantsByCodes: deps.schemas.productVariantsByCodes },
  });
  const productVariantExpanded = makeGetProductVariantExpandedByCode({
    schemas: { productVariantExpanded: deps.schemas.productVariantExpanded },
    adapters: { productVariantExpanded: deps.adapters.productVariantExpanded },
    rawUrlEncode: deps.rawUrlEncode,
  });
  const productVariantPriceHistory = makeGetProductVariantPriceHistory({
    schemas: { productVariantPriceHistory: deps.schemas.productVariantPriceHistory },
    rawUrlEncode: deps.rawUrlEncode,
  });
  const searchProducts = makeGetSearchProducts({
    schemas: { searchProducts: deps.schemas.searchProducts },
  });

  return {
    getAssociatedProducts: associatedProducts.getAssociatedProducts,
    getProductBaseByCode: productBase.getProductBaseByCode,
    getProductByCode: product.getProductByCode,
    getProductExpandedByCode: productExpanded.getProductExpandedByCode,
    getProductsByCodes: productsByCodes.getProductsByCodes,
    requestProductsByContextId: productsByContextId.requestProductsByContextId,
    getProductsByTaxon: productsByTaxon.getProductsByTaxon,
    getProductVariantByCode: productVariant.getProductVariantByCode,
    listProductVariantByCodes: productVariants.listProductVariantByCodes,
    getProductVariantExpandedByCode: productVariantExpanded.getProductVariantExpandedByCode,
    getProductVariantPriceHistory: productVariantPriceHistory.getProductVariantPriceHistory,
    requestSearchProducts: searchProducts.requestSearchProducts,
    GetAssociatedProductsError: associatedProducts.GetAssociatedProductsError,
    GetProductBaseByCodeError: productBase.GetProductBaseByCodeError,
    GetProductByCodeError: product.GetProductByCodeError,
    GetProductExpandedByCodeError: productExpanded.GetProductExpandedByCodeError,
    GetProductsByCodesError: productsByCodes.GetProductsByCodesError,
    GetProductsByContextIdError: productsByContextId.GetProductsByContextIdError,
    GetProductsByTaxonError: productsByTaxon.GetProductsByTaxonError,
    GetProductVariantByCodeError: productVariant.GetProductVariantByCodeError,
    ListProductVariantByCodesError: productVariants.ListProductVariantByCodesError,
    GetProductVariantExpandedByCodeError:
      productVariantExpanded.GetProductVariantExpandedByCodeError,
    GetProductVariantPriceHistoryError:
      productVariantPriceHistory.GetProductVariantPriceHistoryError,
    GetSearchProductsError: searchProducts.GetSearchProductsError,
  };
};

export type ProductGetAssociatedProductsError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetAssociatedProductsError']
>;
export type ProductGetBaseByCodeError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductBaseByCodeError']
>;
export type ProductGetByCodeError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductByCodeError']
>;
export type ProductExpandedError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductExpandedByCodeError']
>;
export type ProductGetProductsByCodesError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductsByCodesError']
>;
export type ProductGetProductsByContextIdError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductsByContextIdError']
>;
export type ProductGetProductsByTaxonError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductsByTaxonError']
>;
export type ProductVariantGetByCodeError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductVariantByCodeError']
>;
export type ProductVariantListByCodesError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['ListProductVariantByCodesError']
>;
export type ProductGetProductVariantExpandedByCodeError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductVariantExpandedByCodeError']
>;
export type ProductVariantGetPriceHistoryError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetProductVariantPriceHistoryError']
>;
export type SearchGetSearchProductsError = BackendApiErrorUnion<
  ReturnType<typeof makeProductModule>['GetSearchProductsError']
>;
